/*
 * Windowing maths for the Earnings Overview bars.
 *
 * Mobile keeps a fixed 80px pitch per period and scrolls the overflow
 * (CustomBarChart.tsx:448-460), so the bar width never changes with the number
 * of periods. Kendo instead divides the plot width by the category count, which
 * makes the bars thinner the more history a provider has. These helpers restore
 * the fixed-pitch behaviour by deriving a category window from the measured
 * width, which the caller applies via ChartCategoryAxisItem min/max.
 */

/* Bar spacing in graph1.svg: centres 88px apart across 8 bars. */
export const PERIOD_PITCH = 88;

export const MIN_VISIBLE_PERIODS = 4;

/* The design shows 8; going wider would drift from it on a large monitor. */
export const MAX_VISIBLE_PERIODS = 8;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * How many periods fit at the fixed pitch, clamped so a narrow card degrades
 * to fewer bars (as mobile does) rather than to one very wide one.
 */
export const resolveVisibleCount = (
  plotWidth,
  { pitch = PERIOD_PITCH, min = MIN_VISIBLE_PERIODS, max = MAX_VISIBLE_PERIODS } = {},
) => {
  if (!plotWidth || plotWidth <= 0) return max;
  return clamp(Math.floor(plotWidth / pitch), min, max);
};

/**
 * Category window centred on the selected period, clamped at both ends so the
 * first and last periods sit flush instead of leaving empty space - the same
 * clamping mobile applies to its scroll offset (CustomBarChart.tsx:506-528).
 *
 * Returns { min, max } as category indices, where max is the exclusive end, so
 * `max - min` is always the window size while the data is longer than it.
 */
export const centerWindow = (selectedIndex, visibleCount, totalCount) => {
  if (!totalCount || visibleCount >= totalCount) {
    return { min: 0, max: totalCount || 0 };
  }

  const safeIndex = Number.isInteger(selectedIndex) && selectedIndex >= 0 ? selectedIndex : 0;
  const lastStart = totalCount - visibleCount;
  const start = clamp(safeIndex - Math.floor(visibleCount / 2), 0, lastStart);

  return { min: start, max: start + visibleCount };
};
